import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import TextQuestionSchema from '@ax2/common/model/schemas/TextQuestionSchema';
import QuestionType from '@ax2/common/model/QuestionType';
import _ from 'lodash';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import { isTemplateFilled } from '@ax2/common/helpers/template';
import config from '../config';

export function getQuestionsValidation(
  questions: QuestionSchema[]
): (string | null)[] {
  return _.map(questions, q => getQuestionValidation(q));
}

export function getQuestionValidation(qSchema: QuestionSchema): string | null {
  if (config.type === 'demo' && qSchema.questionType !== QuestionType.Empty) {
    return null; //Not empty questions are always valid in fast Demo mode
  }

  switch (qSchema.questionType) {
    case QuestionType.Empty:
      return getEmptyTypeQuestionValidation();
    case QuestionType.OpenEndedNumeric:
    case QuestionType.OpenEndedText:
      return getOpenEndedTypeQuestionValidation(qSchema as TextQuestionSchema);
    case QuestionType.TextboxGridNumeric:
    case QuestionType.CheckboxGrid:
    case QuestionType.RadioButtonGrid:
    case QuestionType.RadioButtonGridRow:
      return getGridQuestionValidation(qSchema as GridQuestionSchema);
    case QuestionType.DropdownMenu:
    case QuestionType.CheckboxList:
    case QuestionType.RadioButtonList:
    case QuestionType.ContinuousSumList:
    case QuestionType.TextboxListNumeric:
      return getListQuestionValidation(qSchema as ListQuestionSchema);
    case QuestionType.TextboxListText:
      return getTextListQuestionValidation(qSchema as ListQuestionSchema);
    case QuestionType.RankingList:
      return getRankingQuestionValidation(qSchema as ListQuestionSchema);
    case QuestionType.RatingList:
      return getScaleQuestionValidation(qSchema as GridQuestionSchema);
    default:
      //throw Error(
      //`Unknown QuestionType (${schema.questionType}) was used in question builder.`
      //);
      return null;
  }
}

export function getEmptyTypeQuestionValidation(): string | null {
  return 'Select question type';
}

export function getOpenEndedTypeQuestionValidation(
  qSchema: TextQuestionSchema
): string | null {
  return isTemplateFilled(qSchema.content) ? null : 'Question text is required';
}

export function getGridQuestionValidation(
  qSchema: GridQuestionSchema
): string | null {
  if (!isTemplateFilled(qSchema.content)) {
    return 'Question text is required';
  }
  if (qSchema.columns.length === 0) {
    return 'At least one column required';
  }
  if (qSchema.rows.length === 0) {
    return 'At least one row required';
  }
  if (qSchema.columns.length < 2 || qSchema.rows.length < 2) {
    return 'Specify at least 2 columns and 2 rows';
  }
  if (_.findIndex(qSchema.columns, c => !isTemplateFilled(c.content)) !== -1) {
    return 'All columns must be filled';
  }
  if (_.findIndex(qSchema.rows, r => !isTemplateFilled(r.content)) !== -1) {
    return 'All rows must be filled';
  }
  return null;
}

export function getScaleQuestionValidation(
  qSchema: GridQuestionSchema
): string | null {
  if (!isTemplateFilled(qSchema.content)) {
    return 'Question text is required';
  }
  if (qSchema.columns.length < 2) {
    return 'At least two scale points are required';
  }
  if (
    _.findIndex(
      qSchema.rows,
      r => !!r.content.groups && !isTemplateFilled(r.content)
    ) !== -1
  ) {
    return 'All added variables must be filled';
  }
  return null;
}

export function getListQuestionValidation(
  qSchema: ListQuestionSchema
): string | null {
  if (!isTemplateFilled(qSchema.content)) {
    return 'Question text is required';
  }
  if (qSchema.answerOptions.length <= 1) {
    return 'More than one answer option is required';
  }
  if (
    _.findIndex(qSchema.answerOptions, c => !isTemplateFilled(c.content)) !== -1
  ) {
    return 'All answer options must be filled';
  }
  return null;
}
export function getTextListQuestionValidation(
  qSchema: ListQuestionSchema
): string | null {
  if (!isTemplateFilled(qSchema.content)) {
    return 'Question text is required';
  }
  if (qSchema.answerOptions.length <= 1) {
    return 'More than one answer option is required';
  }
  return null;
}

export function getRankingQuestionValidation(
  qSchema: ListQuestionSchema
): string | null {
  if (!isTemplateFilled(qSchema.content)) {
    return 'Question text is required';
  }
  if (qSchema.answerOptions.length < 2) {
    return 'At least three ranks are required';
  }
  return null;
}
